/* Libraries / React Components / Node_Modules */
import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import moment from "moment";
import { Button, Tooltip } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

/* Local Imports */
import Pagination from "../Pagination/Pagination";
import Loader from "../resuableComponent/loader";

/* Data */
import { 
  fetchQuantumDetails 
} from "../../actions/quantumReadiness";


const QuantumReadinessSystems = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const auth = useSelector((state) => state?.auth?.user);
  const clientType = useSelector((state) => state?.auth?.user?.accessLevel);
  const id = useSelector(
    (state) =>
      state?.summary?.localClient?._id || state?.summary?.clients?.data?.[0]?._id
  );

  // Get quantum data from Redux store
  const pqrData = useSelector((state) => state?.quantumReadiness?.details?.system);

  // =============== State Management ===============
  // Pagination & Filtering
  const [page, setPage] = useState(1);
  const [pageSize, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [filterData, setfilterData] = useState("all");
  const [containerClass, setContainerClass] = useState("data-container");

  // Selection States
  const [selectedIds, setSelectedIds] = useState([]);
  const [multiCheckTrue, setMultiCheckTrue] = useState(false);
  const [checkPath, setCheckPath] = useState('');

  // Modal and Action States
  const [isModal, setModal] = useState("");
  const [forValue, setForValue] = useState("forever");
  const [lookalikeAppId, setLookalikeAppId] = useState([]);
  const [actionNote, setActionNote] = useState("");
  const [action, setAction] = useState("");
  const [selectedValue, setSelectedValue] = useState("pleaseSelect");
  const [showAlgoModal, setShowAlgoModal] = useState(false);
  const [showVulnModal, setShowVulnModal] = useState(false);
  const [modalAlgos, setModalAlgos] = useState([]);
  const [modalVulns, setModalVulns] = useState([]);

  // =============== Effect Hooks ===============
    // Update path when location changes
    useEffect(() => {
      setCheckPath(location.search);
    }, [location.search]);

    // Fetch data when component mounts or dependencies change
    useEffect(() => {
      if (id) {
        fetchQuantumDetails(
          id, 
          dispatch, 
          'system',
          page,
          pageSize,
          search
        );
      }
    }, [id, dispatch, page, pageSize, search, filterData]);

    // Update total items when data changes
    useEffect(() => {
      if (pqrData) {
        setTotalItems(pqrData.totalItems || 0);
      }
    }, [pqrData]);

    // Update container class when data is loaded
    useEffect(() => {
      if (pqrData) {
        setTimeout(() => {
          setContainerClass("data-container active");
        }, 300);
      }
    }, [pqrData]);

  // ====== Computed Properties ======
    const currentTableData = useMemo(() => {
      if (!pqrData || !pqrData.items) return [];
      
      let filteredCategoryData = pqrData.items;
      
      // Apply status filter
      if (filterData !== 'all') {
        filteredCategoryData = filteredCategoryData.filter(pqrCategory => {
          switch (filterData) {
            case 'ready':
              return pqrCategory.status === 'ready';
            case 'at-risk':
              return pqrCategory.status === 'at-risk';
            case 'critical':
              return pqrCategory.status === 'critical';
            default:
              return true;
          }
        });
      }
      
      return filteredCategoryData
        .slice()
        .sort((a, b) => {
          // First: sort by readinessScore ascending (worst first)
          if ((a.readinessScore ?? -1) !== (b.readinessScore ?? -1)) {
            return (a.readinessScore ?? -1) - (b.readinessScore ?? -1);
          }
          // Then: items with encryption array > 0 come before "Not detected"
          const aHasEncryption = a.encryption && a.encryption.length > 0;
          const bHasEncryption = b.encryption && b.encryption.length > 0;
          if (aHasEncryption === bHasEncryption) return 0;
          return aHasEncryption ? -1 : 1;
        });
    }, [pqrData, filterData]);

    // In your render function, before return:
    const categoryOverall = pqrData?.overall || {};
    const pqrScore = categoryOverall?.score ?? pqrData?.score ?? null;
    const pqrRisk = categoryOverall?.riskLevel ?? pqrData?.riskLevel ?? null;
    const pqrStatus = !pqrScore && pqrScore !== 0
      ? 'Not Assessed'
      : pqrScore >= 70
        ? 'Ready'
        : pqrScore >= 40
          ? 'At Risk'
          : 'Critical';

    const statusLightClass =
      !pqrScore && pqrScore !== 0
        ? 'yellow'
        : pqrScore >= 70
          ? 'green'
          : pqrScore >= 40
            ? 'yellow'
            : 'red';

    const totalCount = categoryOverall?.itemCount ?? pqrData?.totalItems ?? 0;
    const atRiskCount = categoryOverall?.atRiskCount ?? pqrData?.atRiskCount ?? 0;
    const readyCount = categoryOverall?.readyCount ?? pqrData?.readyCount ?? 0;

  // ====== Handlers ======
    // Modal handlers
    const addModal = () => {
      setModal("Form Wizard modal");
    };

  // ====== Checkbox Handlers ======
    // Multiple item selection handler
    const multicheck = (data) => {
      if (!data) return;
      
      const allIds = data.map((item) => item._id);
      
      if (selectedIds.length > 0 || selectedIds.length === 0) {
        setMultiCheckTrue(true);
        setSelectedIds(allIds);
      }
      
      if (multiCheckTrue === true) {
        setSelectedIds([]);
        setMultiCheckTrue(false);
      }
    };

    // Single item selection handler
    const singleClick = (id) => {
      setSelectedIds((prevSelectedIds) => {
        if (prevSelectedIds.includes(id)) {
          return prevSelectedIds.filter(selectedId => selectedId !== id);
        } else {
          return [...prevSelectedIds, id];
        }
      });
      
      if (selectedIds.length > 10) {
        setMultiCheckTrue(true);
      } else {
        setMultiCheckTrue(false);
      }
    };

  // ====== Snooze Functions ======
    const snooze = (_id, event) => {
      // Implement snooze logic here if needed
      //console.log("Snooze action:", event, "for ID:", _id);
    };

    const submit = () => {
      // Implement form submission logic here if needed
      setModal("");
      setSelectedValue("pleaseSelect");
      setActionNote("");
      setSelectedIds([]);
    };

  return (
    <div>
      {pqrData ? (
        <div className={containerClass}>
          {/* Header Section */}
          <div className="row clearfix no_margin" style={{ marginTop: '20px' }}>
            <div className="col-sm-2"></div>
            <div className="col-sm-9">
              
              {/* Title */}
              {/* <div className="row mb-3">
                <div className="col-12">
                  <h4>Domain Quantum Readiness</h4>
                  <p className="text-muted">
                    Review all domains in your organization and their quantum readiness status.
                  </p>
                </div>
              </div>*/}

              {/* Status Summary Cards */}
              <div className="row mb-4">
                {/* PQR Status */}
                <div className="col-md-4">
                  <div className="card mb-2">
                    <div className="body status-indicator" style={{ cursor: 'pointer' }}>
                      <div className="d-flex align-items-center">
                        <div className={`status-light ${statusLightClass}`}>
                          <div className="status-pulse"></div>
                        </div>
                        <div className="status-text ml-3">
                          <h6 className="mb-1">PQR Readiness</h6>
                          <p className="text-white mb-0">
                            {pqrStatus}
                            {typeof pqrScore === 'number' && (
                              <span className="ml-2">({pqrScore})</span>
                            )}
                          </p>
                          {categoryOverall?.riskLevel && (
                            <small className="text-muted">
                              Risk Level: {categoryOverall.riskLevel}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Total Domains */}
                <div className="col-md-2">
                  <div className="card">
                    <div className="body d-flex">
                      <div className="mr-3">
                        <i className="fa fa-globe fa-2x text-primary"></i>
                      </div>
                      <div>
                        <h5 className="mb-0">{pqrData?.totalItems || 0}</h5>
                        <span className="text-muted">Total Domains</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Domains at Risk */}
                <div className="col-md-2">
                  <div className="card">
                    <div className="body d-flex">
                      <div className="mr-3">
                        <i className="fa fa-shield-alt fa-2x text-warning"></i>
                      </div>
                      <div>
                        <h5 className="mb-0">{pqrData?.atRiskCount || 0}</h5>
                        <span className="text-muted">At Risk Domains</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* QR Domains */}
                <div className="col-md-2">
                  <div className="card">
                    <div className="body d-flex">
                      <div className="mr-3">
                        <i className="fa fa-check-circle fa-2x text-success"></i>
                      </div>
                      <div>
                        <h5 className="mb-0">{pqrData?.readyCount || 0}</h5>
                        <span className="text-muted">Quantum Ready</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Main Content Section */}
          <div className="row clearfix no_margin" style={{ marginTop: "20px" }}>
            <div className="col-sm-2"></div>
            <div className="col-sm-9" style={{ padding: "0" }}>
              
              <div className="row_boxes row_boxes_table">
                {/* Sort Status Tabs */}
                <ul className="nav nav-tabs mb-2" id="myTabs" role="tablist">
                  {["all", "critical", "at-risk", "ready"].map((cat) => (
                    <li className="nav-item" key={cat}>
                      <a
                        className={`nav-table-tab nav-link ${category === cat ? "active" : ""}`}
                        onClick={() => {
                          setCategory(cat);
                          setPage(1);
                          setfilterData(cat === "all" ? "all" : cat);
                          setSearch("");
                          setSelectedIds([]);
                        }}
                      >
                        {cat === "all" 
                          ? "All Domains" 
                          : cat.split('-')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')}
                      </a>
                    </li>
                  ))}
                </ul>

                {/* Filters Section */}
                <div className="row mb-2">
                  <div className="col-sm-4">
                    <select
                      className="custom-select custom-select-sm form-control form-control-sm"
                      value={filterData}
                      onChange={(e) => { setfilterData(e.target.value); setPage(1) }}
                    >
                      <option value="all">All</option>
                      <option value="ready">Quantum Ready</option>
                      <option value="at-risk">At Risk</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div className="col-sm-4">
                    <div className="mdb-datatable-filter flex-row">
                      <input
                        className="form-control form-control-sm ml-0 my-1"
                        type="text"
                        placeholder="Search domains..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Data Table */}
                <div className="table_box">
                  <table className="table">
                    <thead>
                      <tr>
                        {auth?.accessLevel !== "Threat Analyst" && (
                          <th>
                            <input
                              type="checkbox"
                              checked={selectedIds.length === 0 ? false : multiCheckTrue}
                              onChange={() => multicheck(currentTableData)}
                              className="checkUncheck"
                            />
                          </th>
                        )}
                        <th>#</th>
                        <th>Domain Name</th>
                        <th>Encryption Algorithms</th>
                        <th>Risk Level</th>
                        <th style={{ width: "150px" }}>Readiness Score</th>
                        <th>Vulnerabilities</th>
                        <th>Last Scan</th>
                        {auth?.accessLevel !== "Threat Analyst" && (
                          <th style={{ width: "80px" }}>Action</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {currentTableData?.map((pqrCategory, index) => (
                        <tr key={pqrCategory._id}>
                          {auth?.accessLevel !== "Threat Analyst" && (
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(pqrCategory._id)}
                                onChange={() => singleClick(pqrCategory._id)}
                                className="checkUncheck"
                              />
                            </td>
                          )}
                          <td>{(page - 1) * pageSize + index + 1}</td>
                          <td>
                            <span className="text-warning">
                              {pqrCategory.name || pqrCategory.domainName || "N/A"}
                            </span>
                          </td>
                          <td>
                            {pqrCategory.encryption && pqrCategory.encryption.length > 0 ? (
                              <div>
                                {pqrCategory.encryption.slice(0, 2).map((algo, i) => (
                                  <span 
                                  key={i}
                                  className="badge badge-info mr-1"
                                  >{algo}</span>
                                ))}
                                {pqrCategory.encryption.length > 2 && (
                                  <span
                                  style={{ cursor: "pointer" }}
                                  className="badge badge-light"
                                  onClick={() => {
                                    setModalAlgos(pqrCategory.encryption);
                                    setShowAlgoModal(true);
                                  }}
                                  >+{pqrCategory.encryption.length - 2} more</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">Not detected</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${
                              pqrCategory.riskLevel?.toLowerCase() === 'high' || pqrCategory.riskLevel?.toLowerCase() === 'critical' ? 'badge-danger' :
                              pqrCategory.riskLevel?.toLowerCase() === 'medium' ? 'badge-warning' :
                              'badge-success'
                            }`}>
                              {pqrCategory.riskLevel || 'Unknown'}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                <div 
                                  className={`progress-bar ${
                                    pqrCategory.readinessScore >= 70 ? 'bg-success' :
                                    pqrCategory.readinessScore >= 40 ? 'bg-warning' : 'bg-danger'
                                  }`} 
                                  role="progressbar" 
                                  style={{ width: `${pqrCategory.readinessScore || 0}%` }}
                                  aria-valuenow={pqrCategory.readinessScore || 0} 
                                  aria-valuemin="0" 
                                  aria-valuemax="100">
                                </div>
                              </div>
                              <span className="ml-2">{pqrCategory.readinessScore || 0}</span>
                            </div>
                          </td>
                          <td>
                            {(!!pqrCategory.vulnerabilityCount && !!pqrCategory.vulnerabilities && pqrCategory.vulnerabilities.length > 0) ? (
                              <span
                                style={{ cursor: "pointer" }}
                                className="badge badge-warning"
                                onClick={() => {
                                  setModalVulns(pqrCategory.vulnerabilities || []);
                                  setShowVulnModal(true);
                                }}
                              >
                                {pqrCategory.vulnerabilityCount}
                              </span>
                            ) : (
                              <span className="badge badge-warning" style={{ opacity: 0.6 }}>
                                {pqrCategory.vulnerabilityCount || 0}
                              </span>
                            )}
                          </td>
                          <td style={{ whiteSpace: "unset" }}>
                            <span className="badge badge-success">
                              {moment(pqrCategory.lastScan || pqrCategory.timestamp).format("DD-MM-YYYY")}
                            </span>
                          </td>
                          {auth?.accessLevel !== "Threat Analyst" && (
                            <td>
                              <Box sx={{ width: 200 }}>
                                <FormControl fullWidth>
                                  <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={selectedValue}
                                    onChange={(event) => {
                                      setAction(event.target.value);
                                      snooze(pqrCategory._id, event.target.value);
                                    }}
                                  >
                                    <MenuItem value="pleaseSelect">Action Options</MenuItem>
                                    <MenuItem value="riskAccepted" onClick={() => {
                                      addModal();
                                      setLookalikeAppId([pqrCategory._id]);
                                    }}>
                                      Risk Accepted
                                    </MenuItem>
                                    <MenuItem value="markAsFalsePositive" onClick={() => {
                                      addModal();
                                      setLookalikeAppId([pqrCategory._id]);
                                    }}>
                                      False Positive
                                    </MenuItem>
                                    <MenuItem value="markAsRemidiated">
                                      Remediated
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              </div>

              {/* Pagination */}
              <div className="dataTables_paginate">
                {totalItems > 0 && (
                  <Pagination
                    className="pagination-bar"
                    currentPage={page}
                    totalCount={totalItems}
                    pageSize={pageSize}
                    onPageChange={(page) => setPage(page)}
                  />
                )}
              </div>

            </div>
          </div>

          {/* Modals */}
          {/* Snooze options Modal */}
            <div className={`modal fade Form-Wizard-modal ${isModal === "Form Wizard modal" ? "d-block show" : ""}`}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {action === "markAsFalsePositive" ? "Mark As False Positive" : "Risk Accepted"}
                    </h5>
                    <button 
                      type="button" 
                      onClick={() => {
                        setModal("");
                        setActionNote("");
                      }}
                    >
                      <span>×</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="col-xl-12">
                      <div className="card">
                        <div className="row clearfix">
                          <div className="col-md-12">
                            <label>Snooze for</label>
                            <div className="form-group">
                              <select
                                className="form-control"
                                onChange={(e) => setForValue(e.target.value)}
                              >
                                <option value="forever">Forever</option>
                                <option value="3 months">3 Months</option>
                                <option value="6 months">6 Months</option>
                                <option value="12 months">12 Months</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-md-12">
                            <label>Detail</label>
                            <div className="form-group">
                              <textarea
                                onChange={(e) => setActionNote(e.target.value)}
                                value={actionNote}
                                className="form-control"
                                rows="3"
                              ></textarea>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-12" style={{ marginRight: "28px" }}>
                            <button
                              type="button"
                              onClick={submit}
                              className="btn btn-primary mr-1 pull-right"
                            >
                              Submit
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setModal("");
                                setActionNote("");
                              }}
                              className="btn btn-default pull-right"
                              style={{ marginRight: "10px" }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          {/* Encryption Algorithm Modal */}
          {showAlgoModal && (
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Encryption Algorithms</h5>
                    <button type="button" className="close" onClick={() => setShowAlgoModal(false)}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <ul>
                      {modalAlgos.map((algo, idx) => (
                        <li key={idx}>{algo}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="modal-backdrop show" onClick={() => setShowAlgoModal(false)} />
            </div>
          )}
          {/* Vulnerabilities Modal */}
          {showVulnModal && (
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Vulnerabilities</h5>
                    <button type="button" className="close" onClick={() => setShowVulnModal(false)}>
                      <span>&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    {modalVulns.length === 0 ? (
                      <div>No vulnerabilities found.</div>
                    ) : (
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Risk Level</th>
                            <th>Recommendation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modalVulns.map((vuln, idx) => (
                            <tr key={idx}>
                              <td>{vuln.name}</td>
                              <td>
                                <span className={`badge ${
                                  vuln.riskLevel?.toLowerCase() === 'high' || vuln.riskLevel?.toLowerCase() === 'critical' ? 'badge-danger' :
                                  vuln.riskLevel?.toLowerCase() === 'medium' ? 'badge-warning' :
                                  'badge-success'
                                }`}>
                                  {vuln.riskLevel || 'Unknown'}
                                </span>
                              </td>
                              <td>
                                {vuln.impact}
                                {vuln.recommendation && (
                                  <div><medium className="text-muted">{vuln.recommendation}</medium></div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-backdrop show" onClick={() => setShowVulnModal(false)} />
            </div>
          )}

        </div>
      ) : (
        <Loader />
      )}
      {/* Status Light Styling */}
      <style jsx>{`
        .status-indicator {
          padding: 15px;
        }
        .status-light {
          width: 25px;
          height: 25px;
          border-radius: 50%;
          position: relative;
          box-shadow: 0 0 10px rgba(0,0,0,0.2);
        }
        .status-pulse {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          animation: pulse 2s infinite;
          opacity: 0.6;
        }
        .green { 
          background-color: #2ecc71;
          border: 2px solid #27ae60;
        }
        .yellow { 
          background-color: #f1c40f;
          border: 2px solid #f39c12;
        }
        .red { 
          background-color: #e74c3c;
          border: 2px solid #c0392b;
        }
        .grey { 
          background-color: #95a5a6;
          border: 2px solid #7f8c8d;
        }
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          70% {
            transform: scale(1.5);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }
        .ml-3 {
          margin-left: 15px;
        }
      `}</style>
    </div>
  );
};

export default QuantumReadinessSystems;